import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { breakfastEventsPrefix, pendingInvitesPrefix } from "../queryKeys";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";

/**
 * RSVP via the `handle_breakfast_rsvp` RPC.
 *
 * Used by the detail drawer / event cards. Routes through the SECURITY
 * DEFINER RPC so the DB can bundle status update + notifications.
 *
 * Side effect: on `confirmed`, we upsert a `tracked_days` row for the
 * event's date with `system_day_type_id = 'office'` so the day immediately
 * shows up as Office on the calendar grid.
 */
export function useRespondRsvp() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      eventId: string;
      status: "confirmed" | "declined";
      bringItem?: string;
    }) => {
      if (!params.eventId) throw new Error("Event ID is required");

      const { data, error } = await supabase.rpc("handle_breakfast_rsvp", {
        p_event_id: params.eventId,
        p_status: params.status,
        p_bring_item: params.bringItem || null,
      });

      if (error) throw error;

      // ── RSVP Proactivo: Automatización de Asistencia ──────────────────────
      // Si el usuario acepta, aseguramos que el Dashboard marque "Office"
      if (params.status === "confirmed") {
        const { data: { session } } = await supabase.auth.getSession();
        const currentUserId = session?.user?.id;

        if (currentUserId) {
          const { data: eventData } = await supabase
            .from("breakfast_events")
            .select("event_date")
            .eq("id", params.eventId)
            .single();

          if (eventData) {
            // Upsert: si no existe, crea; si existe, asegura que es 'office'
            const { error: upsertErr } = await supabase
              .from("tracked_days")
              .upsert(
                {
                  user_id: currentUserId,
                  date: eventData.event_date,
                  system_day_type_id: "office",
                  status: "unconfirmed", // Keep as unconfirmed until they actually go.
                },
                { onConflict: "user_id,date" },
              );

            if (upsertErr) {
              console.warn("[Proactive RSVP] Failed to upsert attendance:", upsertErr);
            }
          }
        }
      }

      return data;
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: breakfastEventsPrefix });
      // Snapshot every date-ranged `["breakfastEvents", userId, start, end]`
      // cache slice so `onError` can restore each one — `getQueryData`
      // with a prefix is always `undefined` in TanStack Query 5.
      const previousEventSlices = queryClient.getQueriesData({
        queryKey: breakfastEventsPrefix,
      });

      // Resolve user ID synchronously before entering `.map()` callbacks.
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const currentUserId = currentSession?.user?.id;

      queryClient.setQueriesData(
        { queryKey: breakfastEventsPrefix },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as Array<Record<string, unknown>>)
            .map((ev) => {
              if (ev.id !== params.eventId) return ev;
              const attendees = Array.isArray(ev.attendees)
                ? (ev.attendees as Array<Record<string, unknown>>)
                : [];
              return {
                ...ev,
                attendees: attendees.map((at) => {
                  if (at.user_id !== currentUserId) return at;
                  return {
                    ...at,
                    status: params.status,
                    bring_item: params.bringItem || at.bring_item,
                  };
                }),
              };
            })
            // Mirror the `useBreakfastEvents` visibility filter on the
            // OPTIMISTIC cache so a decline removes the event from the
            // calendar immediately instead of lingering until the
            // server-side invalidation lands. Host rows stay visible
            // (hosts don't "decline" themselves); attendee rows stay
            // only when status is `confirmed` or `removed`.
            .filter((ev) => {
              if (!currentUserId) return true;
              if (ev.host_id === currentUserId) return true;
              const attendees = Array.isArray(ev.attendees)
                ? (ev.attendees as Array<Record<string, unknown>>)
                : [];
              const myRow = attendees.find((a) => a.user_id === currentUserId);
              return (
                myRow?.status === "confirmed" ||
                myRow?.status === "removed"
              );
            });
        },
      );

      return { previousEventSlices };
    },
    onSuccess: () => {
      // Sprint 1.5/2 polish: success is silent. The detail drawer updates
      // (RSVP button state + calendar cell) so a trailing toast was just
      // noise. Errors still surface via `onError`.
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix });
      // cross-feature: circles calendar
      queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
      // cross-feature: attendance roll-up (legacy; no consumer today but
      // retained as a safety net for future roll-up views)
      queryClient.invalidateQueries({ queryKey: ["user-attendance"] });
      // cross-feature: dashboard tracked-days grid
      queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
    },
    onError: (_err, _variables, context) => {
      const ctx = context as
        | { previousEventSlices?: [QueryKey, unknown][] }
        | undefined;
      if (ctx?.previousEventSlices) {
        for (const [key, snap] of ctx.previousEventSlices) {
          queryClient.setQueryData(key, snap);
        }
      }
      toast({
        title: "Failed to update status",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });
}

/**
 * RSVP Center: direct-table status update.
 *
 * Powers the Notification Bell + the "Pending Invites" sidebar card. It
 * bypasses the `handle_breakfast_rsvp` RPC deliberately — direct-table
 * writes are the architecture we've standardised on after repeatedly
 * running into silent RLS / stale-column failures with SECURITY-DEFINER
 * RPCs (see Phase 23).
 *
 *   UPDATE breakfast_attendees
 *      SET status = :status
 *    WHERE event_id = :event_id
 *      AND user_id  = auth.uid()
 *
 * RLS (`ba_update`: `user_id = auth.uid()`) restricts the update to the
 * caller's own row. We `.select()` the affected row and throw if zero rows
 * come back — otherwise Supabase would silently report success when RLS
 * filtered everything out (classic "fake success" bug).
 *
 * Side effect: when a guest accepts (status = `confirmed`), we upsert a
 * `tracked_days` row for the event's date with `system_day_type_id =
 * 'office'` so the day immediately shows up as Office on the calendar
 * grid — matches the behaviour of the `useRespondRsvp` path above.
 */
export function useSetMyRsvp() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      eventId: string;
      status: "confirmed" | "declined";
    }) => {
      if (!params.eventId) throw new Error("Event ID is required");

      const { data: sessionRes } = await supabase.auth.getSession();
      const meId = sessionRes.session?.user?.id;
      if (!meId) throw new Error("Not authenticated");

      const { data: updatedRows, error: updateErr } = await supabase
        .from("breakfast_attendees")
        .update({ status: params.status })
        .eq("event_id", params.eventId)
        .eq("user_id", meId)
        .select("id, event_id, user_id, status");

      if (updateErr) throw new Error(updateErr.message);
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error(
          "Could not update your RSVP. You may not be invited to this event, or the invite has been revoked.",
        );
      }

      // Side effect: mirror acceptances into tracked_days as Office so the
      // calendar grid reflects the commitment immediately.
      if (params.status === "confirmed") {
        const { data: eventData } = await supabase
          .from("breakfast_events")
          .select("event_date")
          .eq("id", params.eventId)
          .single();

        if (eventData?.event_date) {
          const { error: trackedErr } = await supabase
            .from("tracked_days")
            .upsert(
              {
                user_id:            meId,
                date:               eventData.event_date,
                system_day_type_id: "office",
                status:             "unconfirmed",
              },
              { onConflict: "user_id,date" },
            );
          if (trackedErr) {
            console.warn("[setMyRsvp] tracked_days upsert failed:", trackedErr);
          }
        }
      }

      return { eventId: params.eventId, status: params.status };
    },

    // Optimistic: drop the invite from the pending list right away so the
    // card animates out and the calendar picks up the accepted event on the
    // next refetch without the user seeing the invite linger.
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: pendingInvitesPrefix });
      const prevPending = queryClient.getQueriesData({
        queryKey: pendingInvitesPrefix,
      });
      queryClient.setQueriesData(
        { queryKey: pendingInvitesPrefix },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as Array<{ id: string }>).filter(
            (ev) => ev.id !== params.eventId,
          );
        },
      );
      return { prevPending };
    },

    onSuccess: async () => {
      // Sprint 1.5/2 polish: success is silent. The pending invite
      // animates out of the bell and (on accept) the event appears on
      // the calendar — both are stronger confirmations than a toast.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: pendingInvitesPrefix,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: breakfastEventsPrefix,
          refetchType: "all",
        }),
        // cross-feature: event detail drawer
        queryClient.invalidateQueries({
          queryKey: ["eventDetails"],
          refetchType: "all",
        }),
        // cross-feature: circles calendar
        queryClient.invalidateQueries({
          queryKey: circlesSchedulePrefix,
          refetchType: "all",
        }),
        // cross-feature: dashboard tracked-days grid
        queryClient.invalidateQueries({
          queryKey: trackedDaysPrefix,
          refetchType: "all",
        }),
      ]);
    },

    onError: (err: Error, _vars, ctx) => {
      const context = ctx as
        | { prevPending?: Array<[QueryKey, unknown]> }
        | undefined;
      if (context?.prevPending) {
        for (const [key, value] of context.prevPending) {
          queryClient.setQueryData(key, value);
        }
      }
      toast({
        title: "Could not update RSVP",
        description: err?.message || "Please try again later.",
        variant: "destructive",
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: pendingInvitesPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: breakfastEventsPrefix,
        refetchType: "all",
      });
    },
  });
}
